import Text from "common/Typography/Text";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import Space from "antd/es/space";
import Title from "common/Typography/Title";
import { toFixed } from "@shared/utils/numbers";
import { useContext, useEffect } from "react";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";
import { CreateOrderGeneralInfo, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";

interface TriggerPriceInputProps {
  handlePriceChange: (value: string) => void;
  setError: (error: boolean) => void;
  error?: boolean;
  pricePrecision?: number;
  triggerPrice?: string;
  isShort?: boolean;
  isEntry?: boolean;
  exchange?: string;
  generalInfo?: CreateOrderGeneralInfo;
}

const TriggerPriceInput = ({
  handlePriceChange,
  setError,
  isShort,
  error,
  pricePrecision,
  triggerPrice,
  isEntry,
  exchange,
  generalInfo,
}: TriggerPriceInputProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { getExchangePriceFactorLimits } = useContext(
    ExchangesMetadataContext
  )!;
  const currentPrice = generalInfo?.data.current_price || 0;
  const priceFactorLimits = getExchangePriceFactorLimits(exchange);

  const getMinAndMaxLimitPrices = () => {
    const defaultMinPrice = currentPrice / priceFactorLimits.min;
    const defaultMaxPrice = currentPrice * priceFactorLimits.max;
    if (isEntry && !isShort) return [currentPrice, defaultMaxPrice];
    if (isEntry) {
      return isShort
        ? [defaultMinPrice, currentPrice]
        : [currentPrice, defaultMaxPrice];
    } else {
      return isShort
        ? [currentPrice, defaultMaxPrice]
        : [defaultMinPrice, currentPrice];
    }
  };

  const [minPrice, maxPrice] = getMinAndMaxLimitPrices();

  useEffect(() => {
    if (
      !triggerPrice ||
      Number(triggerPrice) > maxPrice ||
      Number(triggerPrice) < minPrice
    ) {
      setError(true);
    } else {
      setError(false);
    }
  }, [triggerPrice, minPrice, maxPrice]);

  return (
    <Space size={4} orientation="vertical" style={{ width: "100%" }}>
      <Title type="secondary" level={4}>
        {t("create_order.trigger_price")}
      </Title>
      <CustomInputNumber
        name={"trigger_price"}
        value={triggerPrice}
        onChange={handlePriceChange}
        precision={pricePrecision}
        stringMode={true}
      />
      {error && (
        <Text type="danger">
          {t("create_order.price_min_max", {
            minPrice: toFixed(minPrice, pricePrecision),
            maxPrice: toFixed(maxPrice, pricePrecision),
          })}
        </Text>
      )}
    </Space>
  );
};

export default TriggerPriceInput;
